import { ref } from 'vue';
import { documentFileKey } from '@qjob/shared';
import { api } from './api';

// Share verified Drive status across the job list and its detail/edit dialogs.
// This avoids a Drive lookup for every row just to render the list shortcuts.
const verifiedFiles = ref(new Map<string, { exists: boolean; checkedAt: number }>());
const verificationTtlMs = 5 * 60 * 1000;

/** Verify persisted Drive links when a job's document panel is opened. */
export function useDocumentAvailability() {
  let requestVersion = 0;

  async function refreshDocumentAvailability(urls: string[]): Promise<void> {
    const version = ++requestVersion;
    const uniqueUrls = [...new Map(
      urls.map((url) => [documentFileKey(url), String(url || '').trim()] as const)
        .filter(([key, url]) => key && url),
    ).values()];

    if (!uniqueUrls.length) return;

    try {
      const result = await api.checkDocumentFiles(uniqueUrls);
      if (version !== requestVersion) return;
      const now = Date.now();
      for (const file of result.files) {
        verifiedFiles.value.set(file.id, { exists: file.exists, checkedAt: now });
      }
    } catch {
      // A transient API/auth error is not evidence that the file was deleted.
    }
  }

  function isDocumentAvailable(url: string): boolean {
    const value = String(url || '').trim();
    if (!value) return false;
    const status = verifiedFiles.value.get(documentFileKey(value));
    if (!status || Date.now() - status.checkedAt > verificationTtlMs) return true;
    return status.exists;
  }

  function isDocumentVerifiedAvailable(url: string): boolean {
    const value = String(url || '').trim();
    if (!value) return false;
    const status = verifiedFiles.value.get(documentFileKey(value));
    return !!status && status.exists && Date.now() - status.checkedAt <= verificationTtlMs;
  }

  return { refreshDocumentAvailability, isDocumentAvailable, isDocumentVerifiedAvailable };
}
